import { useMemo, useContext } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import useCRUD from '../useCRUD';
import useToaster from '../useToaster';
import { QUERY_CACHE_TIME } from '../../constants';
import { LEAVE_TYPE_ACCOUNT_IDS, LEAVE_TYPE_ORGANIZATION_IDS } from '../../utils/accounts';
import useAccount from './useAccount';

const useLeaveTypes = () => {
  const { accountId } = useAccount();
  const { organizationId, enableContractLeaveManagementFeature } = useContext(ConfigContext);
  const queryClient = useQueryClient();
  const { addResponseError, addSuccess } = useToaster();

  // CLM-enabled accounts get leave types regardless of the legacy allowlist.
  const isLeaveTypesEnabled =
    enableContractLeaveManagementFeature ||
    LEAVE_TYPE_ORGANIZATION_IDS.includes(organizationId) ||
    LEAVE_TYPE_ACCOUNT_IDS.includes(accountId);

  const storeKey = ['leaveTypes', accountId];
  const { isLoading, data } = useQuery(storeKey, () => api.fetchLeaveTypes({ accountId }), {
    enabled: !!accountId && isLeaveTypesEnabled,
    cacheTime: QUERY_CACHE_TIME
  });

  const leaveTypes = useMemo(() => data?.data || [], [data?.data]);
  const leaveTypesOptions = useMemo(() => leaveTypes.map(({ id, name }) => ({ value: id, label: name })), [leaveTypes]);

  const { addItem, editItem, deleteItem } = useCRUD(
    storeKey,
    {
      addApi: ({ name, paycode, isPaid }) => api.addLeaveType({ accountId, name, paycode, isPaid }),
      editApi: ({ id, name, paycode, isPaid }) =>
        api.updateLeaveType({ accountId, leaveTypeId: id, name, paycode, isPaid }),
      deleteApi: async ({ id }) => {
        try {
          await api.deleteLeaveType({ accountId, leaveTypeId: id });
          addSuccess('Leave type deleted successfully');
          return true;
        } catch (e) {
          addResponseError(e);
        }
      }
    },
    { onSuccess: () => queryClient.invalidateQueries(storeKey) }
  );

  return {
    isLoading,
    leaveTypes,
    leaveTypesOptions,
    addLeaveType: addItem,
    editLeaveType: editItem,
    deleteLeaveType: deleteItem
  };
};

export default useLeaveTypes;
